import React, { useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Sparkles, Phone } from "lucide-react";
import { SERVICE_CATEGORIES, SALON } from "../data/salonData";

const ServiceItem = ({ name, price }) => (
  <div className="flex items-baseline justify-between gap-3 py-3 border-b border-dashed border-luxuryBlack/10 last:border-0">
    <span className="text-[0.97rem] text-roseGoldDeep font-bold">{name}</span>
    <span className="text-roseGoldDeep font-semibold text-[0.97rem] whitespace-nowrap">
      {price}
    </span>
  </div>
);

const CategoryPanel = ({ cat }) => {
  const waLink = `https://wa.me/${SALON.whatsapp}?text=${encodeURIComponent(
    `Hi Kinjal! I'd like to book a ${cat.label} service.`
  )}`;

  return (
    <div
      data-testid={`service-panel-${cat.id}`}
      className="max-w-4xl mx-auto"
    >
      {/* Header tile */}
      <div className="glass-card rounded-2xl px-6 py-5 mb-10 text-center">
        <div className="label-eyebrow">{cat.label}</div>
        <p className="text-sm sm:text-base text-luxuryBlack/70 mt-1.5">{cat.blurb}</p>
      </div>

      {/* List */}
      <div>
        {cat.items && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12">
            {cat.items.map((it) => (
              <ServiceItem key={it.name} name={it.name} price={it.price} />
            ))}
          </div>
        )}

        {cat.grouped && (
          <div className="space-y-7">
            {cat.grouped.map((g) => (
              <div key={g.brand}>
                <div className="flex items-center gap-3 mb-2">
                  <Sparkles className="w-4 h-4 text-roseGold" />
                  <h4 className="font-display text-lg text-luxuryBlack">
                    {g.brand}
                  </h4>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12">
                  {g.items.map((it) => (
                    <ServiceItem
                      key={`${g.brand}-${it.name}`}
                      name={it.name}
                      price={it.price}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
          <a
            href={`tel:${SALON.phoneTel}`}
            data-testid={`call-${cat.id}`}
            className="btn-primary text-sm"
          >
            <Phone className="w-4 h-4" /> Call to Book {cat.label}
          </a>
          <a
            href={waLink}
            target="_blank"
            rel="noreferrer"
            data-testid={`whatsapp-${cat.id}`}
            className="btn-outline text-sm"
          >
            Enquire on WhatsApp
          </a>
        </div>
      </div>
    </div>
  );
};

export const Services = () => {
  const [tab, setTab] = useState(SERVICE_CATEGORIES[0].id);

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative my-12 mx-3 sm:mx-6 lg:mx-10 rounded-[2.5rem] bg-beigeNude/70 px-4 sm:px-8 lg:px-12 py-20 lg:py-28 overflow-hidden"
    >
      <div className="relative max-w-7xl mx-auto">
        <div className="text-center max-w-2xl mx-auto reveal">
          <span className="label-eyebrow">Our Menu</span>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight text-luxuryBlack">
            Treatments crafted to make you{" "}
            <span className="hero-script">glow</span>.
          </h2>
          <p className="mt-5 text-luxuryBlack/70">
            From quick brow shapes to indulgent premium facials and silky
            keratin rituals — every treatment is delivered with care, hygiene
            and an eye for detail.
          </p>
        </div>

        <div className="mt-12 reveal">
          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <div className="overflow-x-auto no-scrollbar">
              <TabsList
                data-testid="services-tabs-list"
                className="bg-white/70 backdrop-blur-md rounded-full p-1.5 flex gap-1 mx-auto w-max border border-white shadow-soft"
              >
                {SERVICE_CATEGORIES.map((c) => (
                  <TabsTrigger
                    key={c.id}
                    value={c.id}
                    data-testid={`tab-${c.id}`}
                    className="tab-pill rounded-full px-5 py-2.5 text-sm font-medium text-luxuryBlack/70 hover:text-luxuryBlack transition-colors"
                  >
                    {c.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            {SERVICE_CATEGORIES.map((c) => (
              <TabsContent key={c.id} value={c.id} className="mt-12">
                <CategoryPanel cat={c} />
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </div>
    </section>
  );
};

export default Services;
